/**
 * The in-game layer: crosshair, interaction prompt, vitals, subtitles, toasts
 * and the four quick-use slots.
 *
 * Nothing here is permanently on screen. A vital only fades in once it is low
 * enough to matter, the lamp readout only exists while the lamp is on, and the
 * rest is transient. `update` compares numbers and writes to the DOM only when
 * something actually changed.
 */

import { TUNING } from '../core/config';
import { clamp01 } from '../core/math';
import type { EntityMode, EntityState, GameContext } from '../core/types';
import { add, el, icon, meter, replay, type Meter } from './dom';
import { ITEM_UI, type SlotView } from './items';

/** Below this a vital fades in; above it the HUD stays empty. */
const VITAL_SHOW = 40;
const QUICK_SLOTS = 4;

/**
 * Modes that count as something coming for the player. Modes rather than
 * awareness: a hound that has half-noticed a door is not what the noise row is
 * for, and a hound already walking toward the sound very much is.
 */
const HUNTING: ReadonlySet<EntityMode> = new Set<EntityMode>(['investigate', 'stalk', 'chase', 'attack']);
const MAX_TOASTS = 4;
/** Seconds a toast lingers before it starts fading. */
const TOAST_LIFE = 3.4;
const TOAST_FADE = 0.5;

interface VitalRow {
  el: HTMLElement;
  meter: Meter;
  num: HTMLElement;
  shown: boolean;
  crit: boolean;
  last: number;
}

interface Toast {
  el: HTMLElement;
  t: number;
  fading: boolean;
}

interface QuickSlot {
  el: HTMLElement;
  glyph: HTMLElement;
  count: HTMLElement;
  kind: string | null;
  n: number;
}

export class Hud {
  readonly el = el('div', 'ui-hud');

  private readonly health = makeVital('HEALTH');
  private readonly sanity = makeVital('SANITY');
  private readonly lamp = makeVital('LAMP');
  private lampOn = false;

  /**
   * How much noise the player is making. The only number on this HUD they
   * control directly, and the only counterplay to the one entity that reliably
   * kills — so it cannot be left implicit. See `syncNoise`.
   */
  private readonly noise = makeVital('NOISE');
  /** Seconds the noise row stays up after it would otherwise drop away. */
  private noiseHold = 0;

  private readonly promptEl = el('div', 'ui-prompt');
  private readonly promptText = el('span', 'ui-prompt-label');
  private prompt: string | null = null;

  private readonly toastStack = el('div', 'ui-toasts');
  private readonly toasts: Toast[] = [];

  private readonly subtitleEl = el('div', 'ui-subtitle');
  private readonly subtitleSpeaker = el('span', 'ui-subtitle-speaker');
  private readonly subtitleText = el('span', 'ui-subtitle-text');
  private subtitleTime = 0;
  private subtitlesEnabled = true;

  private readonly quick = el('div', 'ui-quick');
  private readonly slots: QuickSlot[] = [];

  constructor() {
    const cross = el('div', 'ui-cross');
    add(cross, el('i', 'ui-cross-dot'), el('i', 'ui-cross-ring'));

    const kbd = el('kbd', undefined, 'E');
    add(this.promptEl, kbd, this.promptText);

    this.toastStack.setAttribute('role', 'status');
    this.toastStack.setAttribute('aria-live', 'polite');
    this.subtitleEl.setAttribute('aria-live', 'polite');
    add(this.subtitleEl, this.subtitleSpeaker, this.subtitleText);

    const vitals = el('div', 'ui-vitals');
    add(vitals, this.lamp.el, this.noise.el, this.health.el, this.sanity.el);
    this.lamp.el.classList.add('lamp');
    this.noise.el.classList.add('noise');

    for (let i = 0; i < QUICK_SLOTS; i++) {
      const slot = el('div', 'ui-quick-slot empty');
      const glyph = el('span', 'ui-quick-glyph');
      const count = el('span', 'ui-quick-count');
      add(slot, el('span', 'ui-quick-key', String(i + 1)), glyph, count);
      this.quick.appendChild(slot);
      this.slots.push({ el: slot, glyph, count, kind: null, n: 0 });
    }

    add(this.el, cross, this.promptEl, this.toastStack, this.subtitleEl, vitals, this.quick);
  }

  /* ------------------------------------------------------------------ *
   * Reactive entry points
   * ------------------------------------------------------------------ */

  setPrompt(text: string | null): void {
    if (text === this.prompt) return;
    this.prompt = text;
    if (text) this.promptText.textContent = text;
    this.promptEl.classList.toggle('on', text !== null);
    this.el.classList.toggle('focused', text !== null);
  }

  toast(text: string, tone: 'info' | 'good' | 'bad' = 'info'): void {
    const node = el('div', `ui-toast ${tone}`, text);
    this.toastStack.appendChild(node);
    this.toasts.push({ el: node, t: TOAST_LIFE, fading: false });
    while (this.toasts.length > MAX_TOASTS) {
      const old = this.toasts.shift();
      old?.el.remove();
    }
  }

  subtitle(text: string, ms?: number, speaker?: string): void {
    if (!this.subtitlesEnabled) return;
    this.subtitleSpeaker.textContent = speaker ? `${speaker}:` : '';
    this.subtitleSpeaker.hidden = !speaker;
    this.subtitleText.textContent = text;
    // Long lines need longer on screen; short ones should not overstay.
    this.subtitleTime = (ms ?? Math.min(7000, 1500 + text.length * 55)) / 1000;
    this.subtitleEl.classList.add('on');
  }

  setSubtitlesEnabled(on: boolean): void {
    this.subtitlesEnabled = on;
    if (!on) {
      this.subtitleTime = 0;
      this.subtitleEl.classList.remove('on');
    }
  }

  /** Called when the inventory contents change; also flashes the bar. */
  setSlots(slots: readonly SlotView[]): void {
    for (let i = 0; i < QUICK_SLOTS; i++) {
      const slot = this.slots[i]!;
      const view = slots[i];
      const kind = view?.kind ?? null;
      const count = view?.count ?? 0;
      if (kind !== slot.kind) {
        slot.kind = kind;
        slot.el.classList.toggle('empty', kind === null);
        if (kind) {
          const def = ITEM_UI[kind];
          slot.glyph.replaceChildren(icon(def.glyph));
          slot.glyph.style.color = def.tint;
          slot.el.title = def.label;
        } else {
          slot.glyph.replaceChildren();
          slot.el.removeAttribute('title');
        }
      }
      if (count !== slot.n) {
        slot.n = count;
        slot.count.textContent = count > 1 ? `x${count}` : '';
      }
    }
    replay(this.quick, 'bump');
  }

  /** Clear everything transient. Called at the start of a run. */
  reset(): void {
    this.setPrompt(null);
    this.noiseHold = 0;
    this.subtitleTime = 0;
    this.subtitleEl.classList.remove('on');
    for (const t of this.toasts) t.el.remove();
    this.toasts.length = 0;
  }

  /* ------------------------------------------------------------------ *
   * Per-frame
   * ------------------------------------------------------------------ */

  update(ctx: GameContext): void {
    const s = ctx.stats;
    this.syncVital(this.health, s.health);
    this.syncVital(this.sanity, s.sanity);

    // The lamp readout exists only while the lamp does.
    const on = ctx.player.flashlightOn;
    if (on !== this.lampOn) {
      this.lampOn = on;
      this.lamp.el.classList.toggle('on', on);
    }
    if (on) this.syncMeter(this.lamp, ctx.player.flashlightCharge, TUNING.flashlight.dyingThreshold);

    const dt = ctx.dt;
    this.syncNoise(ctx, dt);

    // The rest are timers, and timers run on simulation time so a subtitle does
    // not expire behind a pause menu or while the inventory is open.
    if (dt <= 0) return;

    if (this.subtitleTime > 0) {
      this.subtitleTime -= dt;
      if (this.subtitleTime <= 0) this.subtitleEl.classList.remove('on');
    }

    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const t = this.toasts[i]!;
      t.t -= dt;
      if (t.t <= 0) {
        if (!t.fading) {
          t.fading = true;
          t.t = TOAST_FADE;
          t.el.classList.add('out');
        } else {
          t.el.remove();
          this.toasts.splice(i, 1);
        }
      }
    }
  }

  /**
   * The noise row.
   *
   * The hound is blind and hunts by sound, and the only way to survive one is to
   * stop making any. That is not a rule a player can be expected to infer from
   * dying, so this row makes their own noise a thing they can watch: it fades in
   * the first time they sprint, which is long before it can hurt them, and by
   * the time a hound is winding up they already know what fills it.
   *
   * It is forced on while anything is hunting, whatever they are doing. That is
   * the moment the readout has to be there — a player who freezes needs to see
   * the bar fall to nothing, or standing still and getting lucky look identical.
   *
   * It leaks nothing the game does not already say much louder: the growl, the
   * music and the footsteps all announce a hunt well before this row does.
   */
  private syncNoise(ctx: GameContext, dt: number): void {
    const U = TUNING.ui;
    const row = this.noise;
    const value = clamp01(ctx.player.noise);
    const hunted = isHunted(ctx.entities);

    if (value >= U.noiseShow || hunted) this.noiseHold = U.noiseHold;
    else if (dt > 0) this.noiseHold = Math.max(0, this.noiseHold - dt);

    row.meter.set(value);
    const n = Math.round(value * 100);
    if (n !== row.last) {
      row.last = n;
      row.num.textContent = n < 10 ? `0${n}` : String(n);
    }

    const shown = this.noiseHold > 0;
    if (shown !== row.shown) {
      row.shown = shown;
      row.el.classList.toggle('on', shown);
    }
    // Red only when the noise is actually costing them: loud, with something
    // already on its way. Red for merely sprinting would make it wallpaper.
    const crit = hunted && value >= U.noiseLoud;
    if (crit !== row.crit) {
      row.crit = crit;
      row.el.classList.toggle('critical', crit);
    }
  }

  private syncVital(row: VitalRow, value: number): void {
    this.syncMeter(row, value, TUNING.survival.criticalThreshold);
    const shown = value < VITAL_SHOW;
    if (shown !== row.shown) {
      row.shown = shown;
      row.el.classList.toggle('on', shown);
    }
  }

  private syncMeter(row: VitalRow, value: number, critBelow: number): void {
    row.meter.set(value / 100);
    const crit = value < critBelow;
    if (crit !== row.crit) {
      row.crit = crit;
      row.el.classList.toggle('critical', crit);
    }
    const n = Math.max(0, Math.round(value));
    if (n !== row.last) {
      row.last = n;
      row.num.textContent = n < 10 ? `0${n}` : String(n);
    }
  }
}

/** True when something within earshot is actively coming for the player. */
function isHunted(entities: readonly EntityState[]): boolean {
  for (const e of entities) {
    if (e.distToPlayer <= TUNING.ui.noiseHuntRange && HUNTING.has(e.mode)) return true;
  }
  return false;
}

function makeVital(label: string): VitalRow {
  const root = el('div', 'ui-vital');
  const m = meter();
  const num = el('span', 'ui-vital-num', '--');
  add(root, el('span', 'ui-vital-key', label), m.el, num);
  return { el: root, meter: m, num, shown: false, crit: false, last: -1 };
}
